import { LayoutHeader } from '../Types';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIsFreeUser, useNeedsOnboarding } from '../../hooks';
import { NickelsMenuItem } from '../../components/toc/SideNavigation';
import { Hooks } from '@3nickels/data-modules';

export interface IContentViewContext {
  id?: string;
  setId: (id?: string) => void;
  sideNav: (titleSelected?: string) => NickelsMenuItem[];
  header: LayoutHeader;
  setSelectedTab: (selected?: string) => void;
  expanded: boolean;
  toggleExpanded: () => void;
  showUpsell: boolean | undefined;
}

export interface IModalContentViewContext {
  openModal: (key: string, data?: unknown) => void;
  closeModal: (key: string) => void;
  closeAllModals: () => void;
  isModalOpen: (key: string) => boolean;
  getModalData: <T = unknown>(key: string) => T | undefined;
}

type ModalState = {
  data?: unknown;
  open: boolean;
};

const ContentViewContext = createContext<IContentViewContext>({
  setId: () => {},
  sideNav: () => [],
  header: {},
  setSelectedTab: () => {},
  expanded: false,
  toggleExpanded: () => {},
  showUpsell: false,
});

const ModalContentViewContext = createContext<IModalContentViewContext>({
  openModal: () => {},
  closeModal: () => {},
  closeAllModals: () => {},
  isModalOpen: () => false,
  getModalData: () => undefined,
});

export const ContentViewProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const needsOnboarding = useNeedsOnboarding();
  const isFreeUser = useIsFreeUser();
  const productUpsell = Hooks.useProductUpsell();
  const universityEnabled = Hooks.useUniversityEnabled();
  const courses = Hooks.useAllCourses();
  const [id, setId] = useState<string | undefined>();

  const learningNav = useMemo(() => {
    if (courses?.length === 0 && universityEnabled) {
      return '/learning/university';
    } else if (!universityEnabled && courses?.length === 1) {
      return `/learning/${courses[0].courseId}`;
    } else {
      return '/learning';
    }
  }, [courses, universityEnabled]);

  const [header, setHeader] = useState<LayoutHeader>({});
  const [expanded, setExpanded] = useState(true);
  const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), [setExpanded]);
  const { onboardingComplete: personalDetailsCompleted } = Hooks.useOnboardingInfo() ?? {
    onboardingComplete: false,
  };

  const [openModals, setOpenModals] = useState<Record<string, ModalState>>({});
  const openModal = useCallback((key: string, data?: unknown) => {
    setOpenModals((prev) => ({ ...prev, [key]: { data, open: true } }));
  }, []);
  const closeModal = useCallback((key: string) => {
    setOpenModals((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        open: false,
      },
    }));
  }, []);
  const closeAllModals = useCallback(() => {
    setOpenModals((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = {
          ...next[key],
          open: false,
        };
      });
      return next;
    });
  }, []);
  const isModalOpen = useCallback((key: string) => Boolean(openModals[key]?.open), [openModals]);
  const getModalData = useCallback(
    <T = unknown,>(key: string): T | undefined => openModals[key]?.data as T | undefined,
    [openModals]
  );
  const showUpsell = productUpsell?.showInvestmentAdviceUpsell;

  const sideNav = useMemo<(titleSelected?: string) => NickelsMenuItem[]>(
    () => (titleSelected?: string) => {
      const data: NickelsMenuItem[] = [
        {
          title: 'HomeNav',
          icon: 'HomeSvg',
          selected: false,
          href: '/',
        },
        {
          isDivider: true,
        },
        {
          title: 'Accounts',
          icon: 'AccountsSvg',
          selected: false,
          exclude: isFreeUser || needsOnboarding,
          href: '/account-details',
        },
        {
          title: 'Budget',
          icon: 'BudgetSvg',
          selected: false,
          exclude: isFreeUser || needsOnboarding,
          href: '/budget-details',
        },
        {
          title: 'Goals',
          icon: 'GoalsSvg',
          selected: false,
          exclude: isFreeUser || needsOnboarding,
          href: '/goals',
        },
        {
          title: 'Tools',
          icon: 'ToolsSvg',
          selected: false,
          exclude: isFreeUser || needsOnboarding,
          href: '/tools',
        },
        {
          isDivider: true,
          exclude: isFreeUser || needsOnboarding,
        },
        {
          title: 'UnlockPremium',
          icon: 'PremiumSvg',
          behavior: 'modal',
          modalKey: 'upsell',
          exclude: !showUpsell,
        },
        {
          title: 'Learn',
          icon: 'LearnSvg',
          selected: false,
          exclude:
            (!universityEnabled && courses?.length === 0) ||
            (universityEnabled && courses?.length === 0 && isFreeUser),
          href: learningNav,
        },
        {
          title: 'Settings',
          icon: 'SettingsSvg',
          selected: false,
          href: '/settings',
        },
        {
          title: 'ContactUs',
          icon: 'EarPhoneSvg',
          selected: false,
          href: '/contact',
        },
        {
          isDivider: true,
        },
        {
          title: 'SignOut',
          icon: 'SignOutSvg',
          selected: false,
          href: '/logout',
        },
      ];

      return data.map((p) => ({
        ...p,
        selected: p.title === titleSelected,
      }));
    },
    [needsOnboarding, isFreeUser, universityEnabled, courses, learningNav, showUpsell]
  );

  useEffect(() => {
    setHeader((previous) => ({
      ...previous,
      hideProfile: needsOnboarding && !personalDetailsCompleted,
    }));
  }, [needsOnboarding, personalDetailsCompleted]);

  const setSelectedTab = useCallback((selected?: string) => {
    setHeader((previous) => ({ ...previous, selectedTab: selected }));
  }, []);

  const contentViewValue = useMemo<IContentViewContext>(
    () => ({
      id,
      setId,
      sideNav,
      header,
      setSelectedTab,
      expanded,
      toggleExpanded,
      showUpsell,
    }),
    [id, sideNav, header, setSelectedTab, expanded, toggleExpanded, showUpsell]
  );

  const modalContentViewValue = useMemo<IModalContentViewContext>(
    () => ({
      openModal,
      closeModal,
      closeAllModals,
      isModalOpen,
      getModalData,
    }),
    [openModal, closeModal, closeAllModals, isModalOpen, getModalData]
  );

  return (
    <ContentViewContext value={contentViewValue}>
      <ModalContentViewContext value={modalContentViewValue}>{children}</ModalContentViewContext>
    </ContentViewContext>
  );
};

export function useContentView() {
  const context = useContext(ContentViewContext);
  if (!context) {
    throw new Error('useContentView must be used within ContentViewProvider');
  }

  return context;
}

export function useModalContentView() {
  const context = useContext(ModalContentViewContext);
  if (!context) {
    throw new Error('useModalContentView must be used within ContentViewProvider');
  }

  return context;
}
